import {
  ChakraProps,
  Checkbox,
  FormControl,
  FormLabel,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Stack,
  Text,
} from "@chakra-ui/react";
import { SpeakerPassageWithId } from "@worldwidewebb/quest-shared/dist/dialog";
import React, { memo, useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { NodeProps, useReactFlow } from "reactflow";
import { useUpdateNodeData } from "../../../hooks/useUpdateNodeData";
import { useUpdateNodeHandles } from "../../../hooks/useUpdateNodeHandles";
import { NodeData, NodeType } from "../../../models/nodeType";
import OverheadDialogConfigurationModal, {
  OverheadDialogConfiguration,
} from "../../../ui/dialog/OverheadDialogConfigurationModal";
import SelectNpcWithLastUsed from "../../base/SelectNpcWithLastUsed";
import { FlowNodeWithChildren } from "./FlowNode";

interface DialogPreviewProps extends ChakraProps {
  speakerPassages: SpeakerPassageWithId[];
}

const DialogPreview: React.FC<DialogPreviewProps> = ({ color, speakerPassages }) => {
  return (
    <Stack pb={4} spacing={4}>
      {speakerPassages.length !== 0 && (
        <FormControl>
          <FormLabel>
            <Text color={color} casing={"uppercase"}>
              NPC Dialog Passages
            </Text>
          </FormLabel>

          <Stack>
            {speakerPassages.map(({ passageId, speakerId, speakerType, passage, passageType }) => (
              <Stack key={passageId}>
                <Text color={"white"} textAlign={"justify"} whiteSpace={"pre-wrap"} maxW={"md"}>
                  {passage}
                </Text>
              </Stack>
            ))}
          </Stack>
        </FormControl>
      )}
    </Stack>
  );
};

interface SetPopupTextOnNpc extends NodeData {
  npcId: string;
  npcIdSelectionEnabled: boolean;
  speakerPassages: SpeakerPassageWithId[];
  radius: number;
}

const SetPopupTextOnNpcNode: React.FC<NodeProps<NodeType<SetPopupTextOnNpc>>> = (props) => {
  const {
    id: nodeId,
    data: { color, nodeData, targetHandles = [] },
  } = props;

  const npcId = nodeData?.npcId ?? "last_used";
  const npcIdSelectionEnabled = nodeData?.npcIdSelectionEnabled ?? false;
  const speakerPassages = nodeData?.speakerPassages ?? [];
  const radius = nodeData?.radius ?? 96;

  const { register, getValues, setValue, handleSubmit, watch } = useForm<SetPopupTextOnNpc>({
    defaultValues: useMemo(
      () => ({
        npcId,
        npcIdSelectionEnabled,
        radius,
      }),
      [npcId, npcIdSelectionEnabled, radius]
    ),
    mode: "onChange",
  });

  const { updateNodeData } = useUpdateNodeData<SetPopupTextOnNpc>(nodeId);

  const handleUpdate = useCallback(
    (nodeData: SetPopupTextOnNpc) => {
      updateNodeData(nodeData);
    },
    [updateNodeData]
  );

  const { updateNodeTargetHandles } = useUpdateNodeHandles(nodeId);

  const npcIdSelectionEnabledWatched = watch("npcIdSelectionEnabled");

  const reactFlow = useReactFlow();
  const handleUpdateSpeakerPassages = useCallback(
    ({ speakerPassages }: OverheadDialogConfiguration) => {
      reactFlow.setNodes((nodes) => {
        const node = nodes.find(({ id }) => id === nodeId);

        if (node == null) {
          return nodes;
        }

        const nodeDataCloned = structuredClone(node.data) as NodeType;

        const nodeData = (nodeDataCloned.nodeData as SetPopupTextOnNpc) ?? {};
        nodeData.speakerPassages = speakerPassages;

        node.data = {
          ...nodeDataCloned,
          nodeData,
        };

        return nodes;
      });
    },
    [reactFlow]
  );

  useEffect(() => {
    let updatedTargetHandles = targetHandles.slice();

    if (npcIdSelectionEnabledWatched) {
      updatedTargetHandles = updatedTargetHandles.filter(({ handleName }) => handleName !== "npc");
    } else if (!updatedTargetHandles.some(({ handleName }) => handleName === "npc")) {
      updatedTargetHandles.push({
        label: "NPC",
        handleName: "npc",
        handleType: "target",
        handleCategory: "data",
      });
    }

    updateNodeTargetHandles(updatedTargetHandles);
  }, [npcIdSelectionEnabledWatched]);

  return (
    <FlowNodeWithChildren {...props}>
      <form className={"nodrag"} onSubmit={handleSubmit(handleUpdate)} onBlur={handleSubmit(handleUpdate)}>
        <Stack>
          <FormControl>
            <FormLabel>
              <Text color={color} casing={"uppercase"}>
                NPC selection enabled
              </Text>
            </FormLabel>

            <Checkbox color={color} {...register("npcIdSelectionEnabled")} />
          </FormControl>

          {npcIdSelectionEnabledWatched && (
            <SelectNpcWithLastUsed
              value={getValues("npcId")}
              setValue={(value) => setValue("npcId", value)}
              color={color}
            />
          )}

          <FormControl w={"full"}>
            <FormLabel>
              <Text color={color} casing={"uppercase"}>
                NPC Dialog
              </Text>
            </FormLabel>
            <Stack>
              <OverheadDialogConfigurationModal
                color={color}
                speakerId={npcId}
                speakerPassages={speakerPassages}
                onSubmit={handleUpdateSpeakerPassages}
              />
            </Stack>
          </FormControl>

          <FormControl w={"full"}>
            <Stack>
              <DialogPreview color={color} speakerPassages={speakerPassages} />
            </Stack>
          </FormControl>
          <FormControl>
            <FormLabel>
              <Text casing={"uppercase"} color={color}>
                Radius
              </Text>
            </FormLabel>
            <NumberInput isRequired step={1} defaultValue={0} min={0}>
              <NumberInputField id={"radius"} {...register("radius", { valueAsNumber: true })} color={color} />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>
        </Stack>
      </form>
    </FlowNodeWithChildren>
  );
};

export default memo(SetPopupTextOnNpcNode);
